import React, { useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Badge, Input, Button } from "reactstrap";
import { useExtraEvents } from "../Hooks/useExtraEvents";
import { LoadingButton } from "../../../components/Common";

/**
 * ExtraEventCard
 * Componente principal da aba "Extras" na Avaliação.
 * Exibe eventos extras ativos e permite premiar membros com insígnias.
 */
const ExtraEventCard = ({ schedule }) => {
    const classId = schedule?.idClass || null;
    const {
        extraEvents,
        classMembers,
        selectedEvent,
        setSelectedEvent,
        checkedMemberIds,
        awardedMemberIds,
        toggleMember,
        isDirty,
        loading,
        loadingAwarded,
        isSaving,
        handleSave
    } = useExtraEvents({ classId });

    const [searchText, setSearchText] = useState("");

    // Membros filtrados por busca
    const filteredMembers = useMemo(() => {
        const q = (searchText || "").trim().toLowerCase();
        if (!q) return classMembers;
        return classMembers.filter(m => {
            const name = (m.fullName || m.name || "").toLowerCase();
            return name.includes(q);
        });
    }, [classMembers, searchText]);

    // Stats
    const stats = useMemo(() => ({
        total: classMembers.length,
        checked: checkedMemberIds.size,
        newAwards: [...checkedMemberIds].filter(id => !new Set(awardedMemberIds).has(id)).length,
    }), [classMembers, checkedMemberIds, awardedMemberIds]);

    if (loading) {
        return (
            <div className="h-100 flex-grow-1 bg-white rounded shadow-sm border d-flex flex-column align-items-center justify-content-center text-center p-5">
                <div className="spinner-border text-primary mb-4" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Carregando...</span>
                </div>
                <h5 className="text-dark fw-bold mb-2">Carregando eventos</h5>
                <p className="text-muted font-size-15" style={{ maxWidth: '300px' }}>Por favor, aguarde enquanto sincronizamos os eventos extras ativos.</p>
            </div>
        );
    }

    const {
        activityName,
        employeeName,
        startTime,
        endTime,
        color
    } = schedule || {};

    return (
        <Card className="h-100 shadow-sm border-0 bg-light" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {schedule ? (
                <CardHeader className="bg-white px-4 py-4 border-bottom-0 shadow-sm position-relative z-1">
                    {/* Top Accent Line */}
                    {color && <div className="position-absolute top-0 start-0 w-100" style={{ height: '4px', backgroundColor: color }}></div>}

                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Badge color="warning" className="px-2 py-1 fw-medium text-dark" style={{ fontSize: '11px' }}>
                                    EVENTOS EXTRAS
                                </Badge>
                            </div>
                            <h3 className="mb-2 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>{activityName || "Premiação Extra"}</h3>
                            <div className="d-flex align-items-center gap-3">
                                {employeeName && (
                                    <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13">
                                        <i className="mdi mdi-account-circle-outline fs-5" />
                                        <span>{employeeName}</span>
                                    </div>
                                )}
                                <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13 bg-light px-2 py-1 rounded">
                                    <i className="mdi mdi-clock-outline text-primary" />
                                    <span>{startTime} — {endTime}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardHeader>
            ) : (
                <CardHeader className="bg-white px-4 py-4 border-bottom-0 shadow-sm position-relative z-1">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Badge color="warning" className="px-2 py-1 fw-medium text-dark" style={{ fontSize: '11px' }}>
                                    EVENTOS EXTRAS
                                </Badge>
                            </div>
                            <h3 className="mb-2 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>Painel de Recompensas</h3>
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex align-items-center gap-1 text-muted fw-medium font-size-13 bg-light px-2 py-1 rounded">
                                    <i className="mdi mdi-information-outline text-muted" />
                                    <span>Nenhuma turma selecionada</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardHeader>
            )}

            <CardBody className="p-0 d-flex flex-column" style={{ overflow: 'hidden' }}>
                <div className="h-100 overflow-auto bg-white" style={{ minHeight: '400px' }}>
                    {!schedule ? (
                        <div className="p-4 p-md-5 d-flex flex-column h-100">
                            {/* Global Active Extra Events Info */}
                            {extraEvents.length > 0 ? (
                                <div className="bg-white p-4 rounded shadow-sm border border-warning border-opacity-25 position-relative overflow-hidden">
                                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-warning bg-opacity-10" style={{ pointerEvents: 'none' }}></div>
                                    <div className="d-flex align-items-center gap-3 position-relative z-1">
                                        <div className="flex-shrink-0 bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                                            <i className="mdi mdi-star-circle-outline fs-3 text-warning"></i>
                                        </div>
                                        <div>
                                            <Badge color="warning" className="mb-2 fw-medium px-2 py-1 text-dark">Eventos Extras Diários</Badge>
                                            <h5 className="mb-1 fw-bold text-dark">{extraEvents.length} eventos disponíveis</h5>
                                            <div className="text-muted font-size-13">
                                                Selecione uma turma para premiar alunos presentes hoje com insígnias.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white p-4 rounded shadow-sm border border-secondary border-opacity-25">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="flex-shrink-0 bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                                            <i className="mdi mdi-sleep fs-3 text-secondary"></i>
                                        </div>
                                        <div>
                                            <h5 className="mb-1 fw-bold text-dark">Nenhum Evento Extra</h5>
                                            <div className="text-muted font-size-13">
                                                Não há eventos configurados no momento para recompensas extras.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center mt-5">
                                <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-4 text-warning" style={{ width: 80, height: 80 }}>
                                    <i className="mdi mdi-medal-outline" style={{ fontSize: '40px' }} />
                                </div>
                                <h4 className="text-dark fw-bold mb-2">Painel de Recompensas</h4>
                                <p className="text-muted font-size-15" style={{ maxWidth: '300px' }}>Selecione uma turma na lista lateral para iniciar a premiação de insígnias e XP.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="p-3 p-md-4">
                    {/* Lista de eventos extras como cards selecionáveis */}
                    <div className="d-flex flex-wrap gap-2 mb-4">
                        {extraEvents.map(evt => (
                            <div
                                key={evt.id}
                                onClick={() => setSelectedEvent(evt)}
                                className={`d-flex align-items-center gap-2 p-2 rounded-0 border cursor-pointer ${selectedEvent?.id === evt.id ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'bg-light'}`}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                <div
                                    className="rounded-circle border d-flex align-items-center justify-content-center bg-white flex-shrink-0"
                                    style={{ width: 40, height: 40, overflow: 'hidden' }}
                                >
                                    {evt.badgeImageUrl ? (
                                        <img src={evt.badgeImageUrl} alt={evt.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <i className="mdi mdi-medal text-warning font-size-18"></i>
                                    )}
                                </div>
                                <div>
                                    <div className="fw-bold small text-dark">{evt.name}</div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        {evt.startDate} → {evt.endDate}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Checklist de membros */}
                    {selectedEvent ? (
                        loadingAwarded ? (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5 my-3 bg-light rounded-0 border">
                                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2rem', height: '2rem' }}>
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                                <p className="text-muted mb-0 font-size-13">Buscando alunos premiados...</p>
                            </div>
                        ) : <>
                            {/* Header com info do evento e stats */}
                            <div className="d-flex align-items-center justify-content-between mb-3 p-3 bg-light rounded-0 border">
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="rounded-circle border d-flex align-items-center justify-content-center bg-white flex-shrink-0"
                                        style={{ width: 56, height: 56, overflow: 'hidden' }}
                                    >
                                        {selectedEvent.badgeImageUrl ? (
                                            <img src={selectedEvent.badgeImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <i className="mdi mdi-medal text-warning font-size-22"></i>
                                        )}
                                    </div>
                                    <div>
                                        <h6 className="mb-0 fw-bold">{selectedEvent.name}</h6>
                                        {selectedEvent.description && (
                                            <small className="text-muted">{selectedEvent.description}</small>
                                        )}
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-bold text-primary font-size-18">{stats.checked}</div>
                                    <small className="text-muted">de {stats.total} alunos</small>
                                </div>
                            </div>

                            {/* Busca e Controles Laterais */}
                            <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-4">
                                <div className="flex-grow-1" style={{ maxWidth: '400px' }}>
                                    <div className="position-relative">
                                        <i className="mdi mdi-magnify position-absolute text-muted fs-5" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }}></i>
                                        <Input
                                            type="text"
                                            placeholder="Buscar aluno por nome..."
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                            className="bg-light border shadow-sm ps-5 rounded"
                                            style={{ height: '42px' }}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        outline
                                        color="primary"
                                        className="shadow-sm border-primary border-opacity-50 fw-medium d-flex align-items-center gap-1"
                                        onClick={() => {
                                            filteredMembers.forEach(m => {
                                                if (!checkedMemberIds.has(m.id)) toggleMember(m.id);
                                            });
                                        }}
                                    >
                                        <i className="mdi mdi-checkbox-multiple-marked-outline"></i>
                                        <span className="d-none d-md-inline">Todos</span>
                                    </Button>
                                    <Button
                                        outline
                                        color="secondary"
                                        className="shadow-sm border-secondary border-opacity-50 fw-medium d-flex align-items-center gap-1"
                                        onClick={() => {
                                            filteredMembers.forEach(m => {
                                                if (checkedMemberIds.has(m.id)) toggleMember(m.id);
                                            });
                                        }}
                                    >
                                        <i className="mdi mdi-checkbox-multiple-blank-outline"></i>
                                        <span className="d-none d-md-inline">Limpar</span>
                                    </Button>
                                </div>
                            </div>

                            {/* Lista de membros */}
                            <div className="d-flex flex-column gap-3">
                                {filteredMembers.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="mdi mdi-account-search-outline fs-1 text-muted opacity-50 mb-3 d-block"></i>
                                        <p className="text-muted mb-0 font-size-13">Nenhum aluno encontrado.</p>
                                    </div>
                                ) : (
                                    filteredMembers.map(member => {
                                        const isChecked = checkedMemberIds.has(member.id);
                                        const wasAlreadyAwarded = awardedMemberIds.includes(member.id);

                                        return (
                                            <div
                                                key={member.id}
                                                className={`d-flex align-items-center justify-content-between gap-3 p-3 bg-white rounded-0 border shadow-sm ${isChecked ? 'border-warning bg-warning bg-opacity-10' : ''}`}
                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                onClick={() => toggleMember(member.id)}
                                            >
                                                <div className="d-flex align-items-center gap-3 flex-grow-1">
                                                    <Input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        readOnly
                                                        className={`form-check-input flex-shrink-0 ${isChecked ? 'border-warning bg-warning' : ''}`}
                                                        style={{ width: 22, height: 22, pointerEvents: 'none' }}
                                                    />
                                                    <div className="flex-shrink-0">
                                                        {member.photo ? (
                                                            <img
                                                                src={member.photo}
                                                                alt={member.name}
                                                                className="rounded-circle border"
                                                                style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '48px', height: '48px' }}>
                                                                <i className="mdi mdi-account fs-3" style={{ color: '#9e9e9e' }}></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                            <div className="fw-bold text-dark fs-6">{member.name}</div>
                                                            {member.memberNumber && <span className="text-muted small">#{member.memberNumber}</span>}
                                                        </div>
                                                        <div className="d-flex gap-2 align-items-center">
                                                            {member.status && (
                                                                <Badge color={
                                                                    member.status === 'active' ? 'success' :
                                                                        member.status === 'suspended' ? 'warning' : 'secondary'
                                                                } className="border px-2 text-capitalize shadow-none" style={{ fontSize: '10px' }}>
                                                                    {member.status === 'active' ? 'Ativo' : member.status}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    {wasAlreadyAwarded && isChecked && (
                                                        <Badge color="success" className="px-2 py-1 shadow-sm d-flex align-items-center">
                                                            <i className="mdi mdi-check-circle me-1"></i>Premiado
                                                        </Badge>
                                                    )}
                                                    {!wasAlreadyAwarded && isChecked && (
                                                        <Badge color="warning" className="px-2 py-1 shadow-sm text-dark d-flex align-items-center border border-warning">
                                                            <i className="mdi mdi-plus-circle me-1"></i>Novo
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Botão de Salvar no rodapé */}
                            <div className="d-flex justify-content-end align-items-center mt-5 mb-2">
                                <LoadingButton
                                    loading={isSaving}
                                    loadingText="Salvando Evento Extra..."
                                    color="warning"
                                    className="px-4 py-2 shadow-sm fw-bold border"
                                    disabled={!isDirty}
                                    onClick={() => handleSave(selectedEvent?.rewardXp || 0)}
                                >
                                    <i className="mdi mdi-content-save-outline me-2"></i>
                                    Salvar Recompensas
                                </LoadingButton>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-5 bg-light rounded-0 border-dashed mt-4">
                            <div className="rounded-circle bg-white shadow-sm d-inline-flex align-items-center justify-content-center mb-3 text-warning" style={{ width: 64, height: 64 }}>
                                <i className="mdi mdi-medal-outline fs-3"></i>
                            </div>
                            <h5 className="text-dark fw-bold mb-2">Selecione um evento</h5>
                            <p className="text-muted font-size-13 mb-0">Escolha um dos eventos extras acima para ver a lista e premiar os alunos.</p>
                        </div>
                    )}
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default ExtraEventCard;
